import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { typographyVariant } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn } from '@heidi/utils';
import { ChevronDown } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { EnrichedReference } from '../../contracts';
import { useEvidenceAnswerPorts } from './ports';
import { ReferenceList } from './ReferenceCard';

// ============================================================================
// Inline Components
// ============================================================================

/** Cited count label */
const CitedCount = ({ count }: { count: number }) => {
  const { t } = useTranslations();
  const key = count === 1 ? 'chat.citedCount_one' : 'chat.citedCount_other';

  return (
    <span className={cn(typographyVariant({ variant: 'caption' }), 'text-text-tertiary')}>
      {t(key, { count })}
    </span>
  );
};

/** Chevron that rotates when expanded */
const ExpandChevron = ({ isExpanded }: { isExpanded: boolean }) => (
  <ChevronDown
    size={16}
    className={cn('text-text-tertiary transition-transform', isExpanded && 'rotate-180')}
  />
);

/** Header button for the collapsible - forwards props for asChild */
const Header = React.forwardRef<
  HTMLButtonElement,
  {
    label: string;
    count: number;
    isExpanded: boolean;
    compact?: boolean;
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ label, count, isExpanded, compact = false, className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      typographyVariant({ variant: compact ? 'h5' : 'h3' }),
      !compact && 'text-lg sm:text-2xl',
      'flex w-full items-center justify-between rounded-lg py-2 text-text-secondary',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-selected',
      className
    )}
    data-testid="references-button"
    {...props}
  >
    <span className="font-exposure">{label}</span>
    <span className="flex items-center gap-2">
      <CitedCount count={count} />
      <ExpandChevron isExpanded={isExpanded} />
    </span>
  </button>
));
Header.displayName = 'Header';

// ============================================================================
// Main Component
// ============================================================================

interface ReferencesSectionProps {
  references: EnrichedReference[];
  chatId?: string;
  sessionId?: string;
  messageId?: string;
  defaultExpanded?: boolean;
  /** When streaming completes (transitions from true → false), auto-expand if autoExpandOnComplete is true */
  isStreaming?: boolean;
  /** Whether to auto-expand when streaming completes. Disabled in scribing sessions to save space. */
  autoExpandOnComplete?: boolean;
  className?: string;
  /** Whether to use compact styling (smaller header) for in-session views */
  compact?: boolean;
}

/** Collapsible bibliography section for AI responses */
export const ReferencesSection = ({
  references,
  chatId,
  sessionId,
  messageId,
  defaultExpanded = false,
  isStreaming,
  autoExpandOnComplete = false,
  className,
  compact = false,
}: ReferencesSectionProps): JSX.Element | null => {
  const { t } = useTranslations();
  const { trackAnswerEvent } = useEvidenceAnswerPorts();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Track previous streaming state to detect transition from streaming → done
  const wasStreamingRef = useRef(isStreaming);

  // Track toggle event
  const trackToggle = useCallback(
    (newIsExpanded: boolean, trigger: 'manual' | 'auto') => {
      // Legacy event (will be removed after analytics migration)
      trackAnswerEvent({
        type: 'references_section_toggled',
        is_expanded: newIsExpanded,
        references_count: references.length,
        trigger,
        chat_id: chatId,
        message_id: messageId,
      });
      // New object_action event (panel_opened/panel_closed)
      trackAnswerEvent({
        type: 'references_panel_toggled',
        is_expanded: newIsExpanded,
        panel_type: 'references',
        trigger,
        feature: 'evidence',
        references_count: references.length,
        chat_id: chatId,
      });
    },
    [trackAnswerEvent, references.length, chatId, messageId]
  );

  // Handle manual toggle from user interaction
  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsExpanded(open);
      trackToggle(open, 'manual');
    },
    [trackToggle]
  );

  // Auto-expand when streaming completes
  useEffect(() => {
    // Only expand if autoExpandOnComplete is enabled and we transitioned from streaming → done
    if (
      autoExpandOnComplete &&
      wasStreamingRef.current === true &&
      isStreaming === false
    ) {
      setIsExpanded(true);
      trackToggle(true, 'auto');
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming, trackToggle, autoExpandOnComplete]);

  if (!references.length) return null;

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={handleOpenChange}
      className={cn('w-full min-w-0 rounded-lg', className)}
      data-references-section
    >
      <CollapsibleTrigger asChild>
        <Header
          label={t('chat.references')}
          count={references.length}
          isExpanded={isExpanded}
          compact={compact}
        />
      </CollapsibleTrigger>

      <CollapsibleContent className="overflow-hidden will-change-[height,opacity,transform] data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        <ReferenceList
          references={references}
          chatId={chatId}
          sessionId={sessionId}
          messageId={messageId}
          compact={compact}
          isStreaming={isStreaming}
        />
      </CollapsibleContent>
    </Collapsible>
  );
};
